import { GeoJsonLayer } from "@deck.gl/layers";
import * as turf from "@turf/turf";
import { BASE_URL } from "../../config";

const ROI_LAYER_ID = "region-of-interest-selected-layer";

const VOD_TYPENAME = "elektro:vod";
const STUBOVI_TYPENAME = "elektro:stubovi";
const TRAFOSTANICE_TYPENAME = "elektro:trafo_stanice";

const getFeaturesFromLayer = (layer) => {
    const data = layer?.props?.data;

    if (data?.type === "FeatureCollection" && Array.isArray(data.features)) {
        return data.features;
    }

    if (Array.isArray(data?.features)) {
        return data.features;
    }

    if (Array.isArray(data)) {
        return data;
    }

    return [];
};

const normalize = (value) => String(value || "").toLowerCase();

const getFeatureLayerName = (feature, fallback = "") => {
    const id = feature?.id || "";

    if (id.includes(".")) {
        return id.split(".")[0];
    }

    return (
        feature?.properties?.layerName ||
        feature?.properties?.layer ||
        fallback ||
        ""
    );
};

const isStub = (feature, layerName = "") => {
    const name = normalize(layerName);
    const id = normalize(feature?.id);

    return (
        name.includes("stubovi") ||
        id.includes("stubovi") ||
        feature?.properties?.id_stubnog_mjesta !== undefined
    );
};

const isTrafostanica = (feature, layerName = "") => {
    const name = normalize(layerName);
    const id = normalize(feature?.id);

    return (
        name.includes("trafo_stanice") ||
        name.includes("trafostanice") ||
        id.includes("trafo_stanice") ||
        id.includes("trafostanice") ||
        feature?.properties?.id_trafo_stanice !== undefined ||
        feature?.properties?.id_trafostanice !== undefined
    );
};

const isVod = (feature, layerName = "") => {
    const name = normalize(layerName);
    const id = normalize(feature?.id);

    return (
        name.includes("vod") ||
        name.includes("vodovi") ||
        id.includes("vod") ||
        id.includes("vodovi") ||
        feature?.properties?.id_vod !== undefined
    );
};

const cloneFeature = (feature) => {
    if (!feature) return feature;

    return {
        type: feature.type || "Feature",
        id: feature.id,
        geometry: feature.geometry,
        geometry_name: feature.geometry_name,
        properties: feature.properties || {},
    };
};

const getUniqueKey = (feature, layerName = "") => {
    const p = feature?.properties || {};

    return String(
        feature?.id ??
        p.id_stubnog_mjesta ??
        p.id_trafo_stanice ??
        p.id_trafostanice ??
        p.id_vod ??
        `${layerName}-${JSON.stringify(feature?.geometry?.coordinates || [])}`
    );
};

const addUniqueFeature = (array, keys, feature, layerName = "") => {
    const key = getUniqueKey(feature, layerName);

    if (keys.has(key)) return;

    keys.add(key);
    array.push(feature);
};

const createPolygonWkt4326 = (currentMeasureDrawing) => {
    const closedCoordinates = [
        ...currentMeasureDrawing,
        currentMeasureDrawing[0],
    ];

    return `POLYGON((${closedCoordinates
        .map((coord) => `${coord[0]} ${coord[1]}`)
        .join(",")}))`;
};

const buildWfsIntersectsUrl = ({ typeName, polygonWkt4326 }) => {
    const cql = `INTERSECTS(geometrija,SRID=4326;${polygonWkt4326})`;

    return (
        `${BASE_URL}/geoserver/elektro/ows?` +
        `service=WFS` +
        `&version=1.0.0` +
        `&request=GetFeature` +
        `&typeName=${encodeURIComponent(typeName)}` +
        `&outputFormat=${encodeURIComponent("application/json")}` +
        `&srsName=EPSG:4326` +
        `&CQL_FILTER=${encodeURIComponent(cql)}`
    );
};

const fetchGeoServerFeaturesByRoi = async (typeName, polygonWkt4326) => {
    const url = buildWfsIntersectsUrl({
        typeName,
        polygonWkt4326,
    });

    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`GeoServer WFS greška za ${typeName}`);
    }

    const data = await response.json();

    if (!Array.isArray(data?.features)) {
        return [];
    }

    return data.features.map(cloneFeature);
};

const featureTouchesRoiLocal = (feature, roiPolygon4326) => {
    try {
        if (!feature?.geometry || !roiPolygon4326?.geometry) return false;

        const cleanFeature = cloneFeature(feature);

        return turf.booleanIntersects(cleanFeature, roiPolygon4326);
    } catch {
        return false;
    }
};

const createRoiSelectedLayer = (selectedFeatures) => {
    return new GeoJsonLayer({
        id: ROI_LAYER_ID,
        data: {
            type: "FeatureCollection",
            features: selectedFeatures.map(cloneFeature),
        },
        pickable: false,
        stroked: true,
        filled: true,
        visible: true,

        getFillColor: [0, 255, 0, 70],
        getLineColor: [0, 255, 0, 255],
        lineWidthMinPixels: 3,

        pointRadiusMinPixels: 6,
        pointRadiusMaxPixels: 12,
        getPointRadius: 8,
    });
};

export const analyzeRoi = async (
    currentMeasureDrawing,
    setWfsLayers,
    getWfsLayers,
    setRegionStubovi,
    setRegionTrafostanice,
    setRegionVodovi,
    setHoverInfo
) => {
    if (!Array.isArray(currentMeasureDrawing) || currentMeasureDrawing.length < 3) {
        setRegionStubovi([]);
        setRegionTrafostanice([]);
        setRegionVodovi([]);

        setWfsLayers((prevLayers = []) => {
            const safePrevLayers = Array.isArray(prevLayers) ? prevLayers : [];
            return safePrevLayers.filter((layer) => layer?.id !== ROI_LAYER_ID);
        });

        return;
    }

    const polygonWkt4326 = createPolygonWkt4326(currentMeasureDrawing);

    const roiPolygon4326 = {
        type: "Feature",
        geometry: {
            type: "Polygon",
            coordinates: [[
                ...currentMeasureDrawing,
                currentMeasureDrawing[0],
            ]],
        },
        properties: {
            source: "regionOfInterest",
        },
    };

    const selectedVodovi = [];
    const selectedStubovi = [];
    const selectedTrafostanice = [];
    const selectedFeatures = [];

    const vodoviKeys = new Set();
    const stuboviKeys = new Set();
    const trafostaniceKeys = new Set();
    const selectedKeys = new Set();

    try {
        const vodoviFromServer = await fetchGeoServerFeaturesByRoi(
            VOD_TYPENAME,
            polygonWkt4326
        );

        vodoviFromServer.forEach((vod) => {
            if (
                vod?.properties?.id_vod === undefined ||
                vod?.properties?.id_vod === null ||
                vod?.properties?.id_vod === ""
            ) {
                return;
            }

            addUniqueFeature(selectedVodovi, vodoviKeys, vod, "vod");
            addUniqueFeature(selectedFeatures, selectedKeys, vod, "vod");
        });

        try {
            const stuboviFromServer = await fetchGeoServerFeaturesByRoi(
                STUBOVI_TYPENAME,
                polygonWkt4326
            );

            stuboviFromServer.forEach((stub) => {
                addUniqueFeature(selectedStubovi, stuboviKeys, stub, "stubovi");
                addUniqueFeature(selectedFeatures, selectedKeys, stub, "stubovi");
            });
        } catch {
            const layers = typeof getWfsLayers === "function" ? getWfsLayers() : [];

            layers.forEach((layer) => {
                if (!layer || layer.id === ROI_LAYER_ID) return;

                const layerName = layer?.id || layer?.props?.id || "";
                const features = getFeaturesFromLayer(layer);

                features.forEach((feature) => {
                    const cleanFeature = cloneFeature(feature);
                    const featureLayerName = getFeatureLayerName(cleanFeature, layerName);

                    if (!isStub(cleanFeature, featureLayerName)) return;
                    if (!featureTouchesRoiLocal(cleanFeature, roiPolygon4326)) return;

                    addUniqueFeature(selectedStubovi, stuboviKeys, cleanFeature, featureLayerName);
                    addUniqueFeature(selectedFeatures, selectedKeys, cleanFeature, featureLayerName);
                });
            });
        }

        try {
            const trafostaniceFromServer = await fetchGeoServerFeaturesByRoi(
                TRAFOSTANICE_TYPENAME,
                polygonWkt4326
            );

            trafostaniceFromServer.forEach((ts) => {
                addUniqueFeature(selectedTrafostanice, trafostaniceKeys, ts, "trafo_stanice");
                addUniqueFeature(selectedFeatures, selectedKeys, ts, "trafo_stanice");
            });
        } catch {
            const layers = typeof getWfsLayers === "function" ? getWfsLayers() : [];

            layers.forEach((layer) => {
                if (!layer || layer.id === ROI_LAYER_ID) return;

                const layerName = layer?.id || layer?.props?.id || "";
                const features = getFeaturesFromLayer(layer);

                features.forEach((feature) => {
                    const cleanFeature = cloneFeature(feature);
                    const featureLayerName = getFeatureLayerName(cleanFeature, layerName);

                    if (!isTrafostanica(cleanFeature, featureLayerName)) return;
                    if (!featureTouchesRoiLocal(cleanFeature, roiPolygon4326)) return;

                    addUniqueFeature(
                        selectedTrafostanice,
                        trafostaniceKeys,
                        cleanFeature,
                        featureLayerName
                    );
                    addUniqueFeature(selectedFeatures, selectedKeys, cleanFeature, featureLayerName);
                });
            });
        }

        setRegionStubovi(selectedStubovi);
        setRegionTrafostanice(selectedTrafostanice);
        setRegionVodovi(selectedVodovi);

        if (typeof setHoverInfo === "function") {
            setHoverInfo(null);
        }

        const roiSelectedLayer = createRoiSelectedLayer(selectedFeatures);

        setWfsLayers((prevLayers = []) => {
            const safePrevLayers = Array.isArray(prevLayers) ? prevLayers : [];

            return [
                ...safePrevLayers.filter((layer) => layer?.id !== ROI_LAYER_ID),
                roiSelectedLayer,
            ];
        });
    } catch (error) {
        console.error("Greška pri ROI GeoServer analizi:", error);

        setRegionStubovi([]);
        setRegionTrafostanice([]);
        setRegionVodovi([]);

        setWfsLayers((prevLayers = []) => {
            const safePrevLayers = Array.isArray(prevLayers) ? prevLayers : [];
            return safePrevLayers.filter((layer) => layer?.id !== ROI_LAYER_ID);
        });
    }
};

export const cancelSelectedRoi = (
    setWfsLayers,
    setRegionStubovi,
    setRegionTrafostanice,
    setRegionVodovi,
    setHoverInfo
) => {
    setRegionStubovi([]);
    setRegionTrafostanice([]);
    setRegionVodovi([]);

    if (typeof setHoverInfo === "function") {
        setHoverInfo(null);
    }

    setWfsLayers((prevLayers = []) => {
        const safePrevLayers = Array.isArray(prevLayers) ? prevLayers : [];

        return safePrevLayers.filter((layer) => layer?.id !== ROI_LAYER_ID);
    });
};